/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * ML-KEM-768 wrapper. Wraps `@noble/post-quantum/ml-kem.js` to provide a
 * stable wrapper-API call shape, route every random byte through the F3
 * RNG seam (`src/services/random.ts`), and validate input sizes at the
 * wrapper boundary (#102) so misuse fails with typed errors rather than
 * an implementation-specific noble crash. Production via `defaultRandom`,
 * tests via `seededRandom(seed)`.
 *
 * Why ML-KEM-768 (NIST level 3) over ML-KEM-1024 (level 5)? Smaller keys
 * and ciphertexts, faster operations, still post-quantum. The redesign
 * locks on level 3 across the create-message pipeline.
 */

import { ml_kem768 } from '@noble/post-quantum/ml-kem.js';
import type { Random } from '../random.js';

const KEYGEN_SEED_BYTES = 64;
const ENCAPSULATE_RAND_BYTES = 32;

/** Fixed byte lengths per FIPS 203 ML-KEM-768 parameter set. */
const ML_KEM_PUBLIC_KEY_BYTES = 1184;
const ML_KEM_PRIVATE_KEY_BYTES = 2400;
const ML_KEM_CIPHERTEXT_BYTES = 1088;

/** Thrown when an ML-KEM-768 public key of the wrong length is supplied to `encapsulate`. */
export class InvalidMlKemPublicKeyError extends Error {
  constructor(actual: number) {
    super(
      `ML-KEM-768 public key must be ${ML_KEM_PUBLIC_KEY_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidMlKemPublicKeyError';
  }
}

/** Thrown when an ML-KEM-768 private key of the wrong length is supplied to `decapsulate`. */
export class InvalidMlKemPrivateKeyError extends Error {
  constructor(actual: number) {
    super(
      `ML-KEM-768 private key must be ${ML_KEM_PRIVATE_KEY_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidMlKemPrivateKeyError';
  }
}

/** Thrown when an ML-KEM-768 ciphertext of the wrong length is supplied to `decapsulate`. */
export class InvalidMlKemCiphertextError extends Error {
  constructor(actual: number) {
    super(
      `ML-KEM-768 ciphertext must be ${ML_KEM_CIPHERTEXT_BYTES} bytes, got ${actual}`
    );
    this.name = 'InvalidMlKemCiphertextError';
  }
}

const requirePublicKey = (k: Uint8Array): void => {
  if (k.length !== ML_KEM_PUBLIC_KEY_BYTES) {
    throw new InvalidMlKemPublicKeyError(k.length);
  }
};

const requirePrivateKey = (k: Uint8Array): void => {
  if (k.length !== ML_KEM_PRIVATE_KEY_BYTES) {
    throw new InvalidMlKemPrivateKeyError(k.length);
  }
};

const requireCiphertext = (c: Uint8Array): void => {
  if (c.length !== ML_KEM_CIPHERTEXT_BYTES) {
    throw new InvalidMlKemCiphertextError(c.length);
  }
};

export interface MlKemKeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

export interface MlKemEncapsulation {
  sharedSecret: Uint8Array;
  ciphertext: Uint8Array;
}

/**
 * Generate an ML-KEM-768 keypair.
 *
 * @param rng - randomness source (use `defaultRandom` in production)
 * @returns 1184-byte public key + 2400-byte private key
 */
export const keyGen = (rng: Random): MlKemKeyPair => {
  const seed = rng.getRandomBytes(KEYGEN_SEED_BYTES);
  const { secretKey, publicKey } = ml_kem768.keygen(seed);
  return { privateKey: secretKey, publicKey };
};

/**
 * Encapsulate a fresh shared secret to a recipient public key.
 *
 * @param rng - randomness source for the encapsulation seed
 * @param publicKey - 1184-byte recipient public key
 * @returns 32-byte shared secret + 1088-byte ciphertext to send to the recipient
 * @throws {InvalidMlKemPublicKeyError} if `publicKey.length !== 1184`
 */
export const encapsulate = (
  rng: Random,
  publicKey: Uint8Array
): MlKemEncapsulation => {
  requirePublicKey(publicKey);
  const randomness = rng.getRandomBytes(ENCAPSULATE_RAND_BYTES);
  const { cipherText, sharedSecret } = ml_kem768.encapsulate(
    publicKey,
    randomness
  );
  return { sharedSecret, ciphertext: cipherText };
};

/**
 * Recover the shared secret from a ciphertext using the recipient's
 * private key. Per FIPS 203, decapsulation never throws on tampered
 * ciphertexts — it deterministically returns *some* shared secret derived
 * from the secret key (implicit rejection). The encapsulator and
 * decapsulator therefore disagree on the secret, which surfaces as an AEAD
 * tag failure when the secret is later used as a symmetric key.
 *
 * Length mismatches are a separate concern from the implicit-rejection
 * path — a wrong-length input is a caller bug, not an attacker-supplied
 * tampered ciphertext, and we throw a typed error rather than ever
 * reaching noble's decapsulator with a malformed input.
 *
 * @param privateKey - 2400-byte recipient private key
 * @param ciphertext - 1088-byte KEM ciphertext from `encapsulate`
 * @returns 32-byte shared secret
 * @throws {InvalidMlKemPrivateKeyError} if `privateKey.length !== 2400`
 * @throws {InvalidMlKemCiphertextError} if `ciphertext.length !== 1088`
 */
export const decapsulate = (
  privateKey: Uint8Array,
  ciphertext: Uint8Array
): Uint8Array => {
  requirePrivateKey(privateKey);
  requireCiphertext(ciphertext);
  return ml_kem768.decapsulate(ciphertext, privateKey);
};
